import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import {
    Badge,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay
} from '@chakra-ui/react';
import { ImHammer2 } from 'react-icons/im';
import { FiFileText } from 'react-icons/fi';
import { IoCloseCircle } from 'react-icons/io5';

import { formatDateTimeString } from 'common/helpers';
import { Button } from 'common/materials';
import { ActivityParticipantMessage, TaskActivity, TaskStatusList } from 'data/link/schemas';
import { TicketListItem } from 'models/link/Link';
import { ReplyEmailSupport } from 'models/user/UserSupport';

import ParticipantMessage from './ParticipantMessage';

// Temporarily hidden until the Center task-progress API is ready for use.
const IS_TASK_PROGRESS_UPDATE_ENABLED = false;

type Props = {
    isOpen: boolean;
    task?: TicketListItem;
    taskActivity?: TaskActivity;
    currentLinkId: string;
    onClose: () => void;
    onReplyEmail: (reply: ReplyEmailSupport) => void;
    onSubmitComment?: (input: CommentReplyInput) => Promise<boolean>;
    onClickCloseTask: () => void;
    onClickCancelTask: () => void;
    onUpdateProgress: (code: string) => Promise<boolean>;
};

export type CommentReplyInput = {
    message: string;
    objectLink: string;
    replyToMessageKey?: string;
};

type ReadOnlyFieldProps = {
    label: string;
    value?: React.ReactNode;
    className?: string;
    accent?: boolean;
};

function ReadOnlyField({ label, value, className = '', accent = false }: ReadOnlyFieldProps) {
    const hasValue = value !== undefined && value !== null && value !== '';

    return (
        <div className={className}>
            <p className="mb-1 text-sm font-medium text-neutral-700">{label}</p>
            <div
                className={`min-h-[38px] rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-800 ${
                    accent ? 'border-l-4 border-l-primary-900' : ''
                }`}
            >
                {hasValue ? value : '-'}
            </div>
        </div>
    );
}

function getInitials(name?: string | null) {
    if (!name?.trim()) return '?';

    return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part.charAt(0))
        .join('')
        .toUpperCase();
}

function getAmazonAwsPictureUrl(picture?: string | null) {
    if (!picture) return undefined;

    try {
        const url = new URL(picture);
        const hostname = url.hostname.toLowerCase();
        const isAmazonAwsHost =
            hostname === 'amazonaws.com' || hostname.endsWith('.amazonaws.com');

        return url.protocol === 'https:' && isAmazonAwsHost ? url.toString() : undefined;
    } catch {
        return undefined;
    }
}

function ParticipantAvatar({ name, picture }: { name: string; picture?: string | null }) {
    const pictureUrl = getAmazonAwsPictureUrl(picture);
    const [hasImageError, setHasImageError] = useState(false);

    useEffect(() => {
        setHasImageError(false);
    }, [pictureUrl]);

    if (pictureUrl && !hasImageError) {
        return (
            <img
                src={pictureUrl}
                alt={name}
                loading="lazy"
                className="h-7 w-7 shrink-0 rounded-full object-cover"
                onError={() => setHasImageError(true)}
            />
        );
    }

    return (
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-900">
            {getInitials(name)}
        </span>
    );
}

function getStatusClassName(status?: string | null) {
    const normalizedStatus = status?.trim().toLowerCase() ?? '';

    if (
        normalizedStatus.includes('ยกเลิก') ||
        normalizedStatus.includes('cancel') ||
        normalizedStatus.includes('reject')
    ) {
        return 'border border-red-200 bg-red-50 text-red-600';
    }

    if (
        normalizedStatus.includes('ปิดงาน') ||
        normalizedStatus.includes('เสร็จ') ||
        normalizedStatus.includes('complete') ||
        normalizedStatus.includes('checkedout') ||
        normalizedStatus.includes('done') ||
        normalizedStatus.includes('success')
    ) {
        return 'border border-green-200 bg-green-50 text-green-600';
    }

    if (
        normalizedStatus.includes('กำลัง') ||
        normalizedStatus.includes('progress') ||
        normalizedStatus.includes('process') ||
        normalizedStatus.includes('working') ||
        normalizedStatus.includes('doing')
    ) {
        return 'border border-blue-200 bg-blue-50 text-blue-600';
    }

    if (
        normalizedStatus.includes('รอ') ||
        normalizedStatus.includes('pending') ||
        normalizedStatus.includes('waiting') ||
        normalizedStatus.includes('open')
    ) {
        return 'border border-yellow-200 bg-yellow-50 text-yellow-600';
    }

    return 'border border-neutral-200 bg-neutral-100 text-neutral-600';
}

function StatusBadge({ status }: { status?: string | null }) {
    return (
        <Badge
            className={`rounded-full px-3 py-1 text-xs font-semibold normal-case ${getStatusClassName(
                status
            )}`}
        >
            {status || '-'}
        </Badge>
    );
}

function ProgressField({ value, description }: { value?: string | null; description?: string }) {
    const parsedValue = Number.parseFloat(value ?? '');
    const hasProgress = Number.isFinite(parsedValue);
    const percentage = hasProgress ? Math.min(100, Math.max(0, parsedValue)) : 0;

    return (
        <div>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm">
                <p className="font-medium text-neutral-700">ความคืบหน้า</p>
                <div className="ml-auto flex min-w-0 items-center gap-2">
                    {description && (
                        <span className="truncate text-neutral-500">{description}</span>
                    )}
                    <span className="shrink-0 font-semibold text-primary-900">
                        {hasProgress ? `${percentage}%` : '-'}
                    </span>
                </div>
            </div>
            <div className="flex min-h-[26px] items-center">
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-neutral-200">
                    <div
                        className="h-full rounded-full bg-primary-900 transition-[width] duration-300"
                        style={{ width: `${percentage}%` }}
                    />
                </div>
            </div>
        </div>
    );
}

type ProgressEditorProps = {
    currentCode?: string | null;
    description?: string;
    disabled?: boolean;
    options: TaskStatusList[];
    value?: string | null;
    onSave: (code: string) => Promise<boolean>;
};

function ProgressEditor(props: ProgressEditorProps) {
    const { currentCode, description, disabled, options, value, onSave } = props;
    const validOptions = useMemo(
        () => options.filter(option => Boolean(option.status_code)),
        [options]
    );
    const defaultCode = currentCode || validOptions[0]?.status_code || '';
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedCode, setSelectedCode] = useState(defaultCode);

    useEffect(() => {
        setSelectedCode(defaultCode);
        setIsEditing(false);
    }, [defaultCode]);

    const selectedOption = validOptions.find(option => option.status_code === selectedCode);
    const handleSave = async () => {
        if (!selectedCode || selectedCode === currentCode) return;

        setIsSaving(true);
        try {
            if (await onSave(selectedCode)) setIsEditing(false);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div>
            <ProgressField
                value={isEditing ? selectedOption?.percentage || value : value}
                description={isEditing ? selectedOption?.description || description : description}
            />

            {!disabled && validOptions.length > 0 && (
                <div className="mt-2 flex items-center justify-end gap-2">
                    {isEditing ? (
                        <React.Fragment>
                            <select
                                value={selectedCode}
                                disabled={isSaving}
                                onChange={event => setSelectedCode(event.target.value)}
                                className="min-w-0 flex-1 rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-800"
                            >
                                {validOptions.map(option => (
                                    <option
                                        key={option.status_code!}
                                        value={option.status_code!}
                                    >
                                        {option.description || option.status_code}
                                        {option.percentage
                                            ? ` — ${option.percentage.replace(/%$/, '')}%`
                                            : ''}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                disabled={isSaving || !selectedCode || selectedCode === currentCode}
                                onClick={handleSave}
                                className="rounded-md bg-primary-900 px-3 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {isSaving ? 'กำลังบันทึก...' : 'บันทึก'}
                            </button>
                            <button
                                type="button"
                                disabled={isSaving}
                                onClick={() => {
                                    setSelectedCode(defaultCode);
                                    setIsEditing(false);
                                }}
                                className="rounded-md border border-neutral-300 px-3 py-2 text-sm text-neutral-700 disabled:opacity-50"
                            >
                                ยกเลิก
                            </button>
                        </React.Fragment>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setIsEditing(true)}
                            className="text-sm font-medium text-primary-900 hover:underline"
                        >
                            แก้ไขความคืบหน้า
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function TicketDetailModal(props: Props) {
    const {
        isOpen,
        task,
        taskActivity,
        currentLinkId,
        onClose,
        onReplyEmail,
        onSubmitComment,
        onClickCloseTask,
        onClickCancelTask,
        onUpdateProgress
    } = props;
    const [commentMessage, setCommentMessage] = useState('');
    const [replyTarget, setReplyTarget] = useState<ActivityParticipantMessage>();
    const [isSubmittingComment, setIsSubmittingComment] = useState(false);
    const chatListRef = useRef<HTMLDivElement>(null);

    const activities = useMemo(
        () => [...(taskActivity?.activity_participant_message ?? [])],
        [taskActivity]
    );
    const conversationActivities = useMemo(
        () =>
            activities.filter(
                activity =>
                    activity.data ||
                    activity.messageText ||
                    activity.remarkEventDetail ||
                    activity.eventMessage ||
                    activity.files?.length
            ),
        [activities]
    );
    const latestConversationKey =
        conversationActivities[conversationActivities.length - 1]?.key;

    useEffect(() => {
        setCommentMessage('');
        setReplyTarget(undefined);
        setIsSubmittingComment(false);
    }, [isOpen, task?.aobjectlink]);

    useEffect(() => {
        if (!isOpen || !conversationActivities.length) return;

        const frameId = window.requestAnimationFrame(() => {
            const chatList = chatListRef.current;
            if (chatList) chatList.scrollTop = chatList.scrollHeight;
        });

        return () => window.cancelAnimationFrame(frameId);
    }, [isOpen, task?.aobjectlink, latestConversationKey, conversationActivities.length]);

    const handleSubmitComment = async () => {
        const message = commentMessage.trim();
        if (!task || !message || !onSubmitComment || isSubmittingComment) return;

        setIsSubmittingComment(true);
        try {
            const isSuccess = await onSubmitComment({
                message,
                objectLink: task.aobjectlink,
                replyToMessageKey: replyTarget?.key || undefined
            });

            if (isSuccess) {
                setCommentMessage('');
                setReplyTarget(undefined);
            }
        } finally {
            setIsSubmittingComment(false);
        }
    };

    if (!task || !taskActivity) return null;

    const participants = taskActivity.activity_participant ?? [];
    const normalizedTaskStatus = task.activityStatus?.toLowerCase() ?? '';
    const canUpdateTask =
        !normalizedTaskStatus.includes('checkedout') &&
        !normalizedTaskStatus.includes('canceled');
    const currentStatusItem = taskActivity.task_status_list?.find(
        status => status.status_code === taskActivity.status
    );
    const currentStatus =
        currentStatusItem?.description || task.activityOverviewStatus || taskActivity.status || '-';
    const currentProgress = currentStatusItem?.percentage || task.taskStatusPercent;
    const currentProgressDescription = currentStatusItem?.description || task.taskStatusDesc;
    const ticketNumber = taskActivity.ticket?.ticket_no || task.ticketNO || '-';
    const hasteDescription =
        taskActivity.haste_list?.find(haste => haste.haste_code === taskActivity.haste)
            ?.description ||
        task.haste ||
        taskActivity.haste;
    return (
        <Modal
            size="6xl"
            isOpen={isOpen}
            onClose={onClose}
        >
            <ModalOverlay />
            <ModalContent className="max-h-[calc(100vh-80px)] w-[calc(100vw-48px)] max-w-[1280px] overflow-hidden">
                <ModalHeader className="border-b px-6 py-4 pr-14">
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-50 text-primary-900">
                            <FiFileText size={20} />
                        </span>
                        <div>
                            <p className="text-xl font-semibold text-neutral-900">รายละเอียดงาน</p>
                        </div>
                        <span className="rounded-md border border-primary-200 bg-primary-50 px-3 py-1.5 text-sm font-semibold text-primary-900">
                            Ticket No. {ticketNumber}
                        </span>
                        <StatusBadge status={currentStatus} />

                        {canUpdateTask && (
                            <div className="ml-auto flex gap-2 pr-2">
                                <Button
                                    onClick={onClickCloseTask}
                                    className="gap-2 bg-primary-900 text-white"
                                >
                                    <ImHammer2 size={16} />
                                    ปิดงาน
                                </Button>
                                <Button
                                    outline
                                    onClick={onClickCancelTask}
                                    className="gap-2 border-danger-600 text-danger-600"
                                >
                                    <IoCloseCircle size={16} />
                                    ยกเลิกงาน
                                </Button>
                            </div>
                        )}
                    </div>
                </ModalHeader>
                <ModalCloseButton className="right-4 top-4" />

                <ModalBody className="overflow-y-auto px-6 py-5">
                    <div className="space-y-6">
                        <section>
                            <p className="mb-3 text-base font-semibold text-neutral-800">
                                ข้อมูลงาน
                            </p>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <ReadOnlyField
                                    accent
                                    label="Work Group"
                                    value={taskActivity.prj_desc}
                                />
                                <ReadOnlyField
                                    accent
                                    label="Sub Work Group"
                                    value="-"
                                />
                            </div>

                            <ReadOnlyField
                                accent
                                label="หัวข้องาน"
                                value={taskActivity.subject || task.jobdescription}
                                className="mt-4"
                            />

                            <div className="mt-4">
                                <p className="mb-1 text-sm font-medium text-neutral-700">
                                    รายละเอียดงาน
                                </p>
                                <div className="min-h-[72px] whitespace-pre-wrap rounded-lg border border-l-4 border-neutral-200 border-l-primary-900 bg-neutral-50 px-3 py-2 text-sm text-neutral-800">
                                    {taskActivity.detail || task.ticketDetail || '-'}
                                </div>
                            </div>
                        </section>

                        <section className="border-t pt-5">
                            <p className="mb-3 text-base font-semibold text-neutral-800">
                                มอบหมายผู้รับผิดชอบ
                            </p>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <ReadOnlyField
                                    label="ผู้สร้างงาน"
                                    value={taskActivity.created_by_name || task.creator}
                                />
                                <ReadOnlyField
                                    label="ผู้รับมอบหมาย"
                                    value={
                                        taskActivity.first_assign_name ||
                                        task.leaderEN ||
                                        task.leader
                                    }
                                />
                            </div>

                            <div className="mt-4">
                                <p className="mb-1 text-sm font-medium text-neutral-700">
                                    ผู้เกี่ยวข้อง
                                </p>
                                <div className="grid min-h-[46px] max-h-[136px] grid-cols-1 gap-2 overflow-y-auto rounded-md border border-neutral-200 bg-neutral-50 p-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                                    {participants.length ? (
                                        participants.map((participant, index) => {
                                            const name =
                                                participant.full_name || participant.link_id || '-';
                                            return (
                                                <div
                                                    key={
                                                        participant.key ||
                                                        participant.link_id ||
                                                        index
                                                    }
                                                    className="flex h-14 min-w-0 items-center gap-2 overflow-hidden rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-sm"
                                                >
                                                    <ParticipantAvatar
                                                        name={name}
                                                        picture={participant.picture}
                                                    />
                                                    <span
                                                        title={name}
                                                        className="line-clamp-2 min-w-0 flex-1 break-words leading-4"
                                                    >
                                                        {name}
                                                    </span>
                                                    {participant.status && (
                                                        <span className="shrink-0 text-xs text-neutral-400">
                                                            {participant.status}
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <span className="self-center text-sm text-neutral-500">
                                            ไม่มีผู้เกี่ยวข้อง
                                        </span>
                                    )}
                                </div>
                            </div>
                        </section>

                        <section className="border-t pt-5">
                            <p className="mb-3 text-base font-semibold text-neutral-800">
                                กำหนดการและสถานะ
                            </p>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div>
                                    <p className="mb-2 text-sm font-medium text-neutral-700">
                                        สถานะปัจจุบัน
                                    </p>
                                    <div className="flex min-h-[26px] items-center">
                                        <StatusBadge status={currentStatus} />
                                    </div>
                                </div>
                                <ProgressEditor
                                    currentCode={taskActivity.status}
                                    value={currentProgress}
                                    description={currentProgressDescription}
                                    options={taskActivity.task_status_list ?? []}
                                    disabled={
                                        !IS_TASK_PROGRESS_UPDATE_ENABLED || !canUpdateTask
                                    }
                                    onSave={onUpdateProgress}
                                />
                                <ReadOnlyField
                                    label="วันและเวลาเริ่ม"
                                    value={formatDateTimeString(
                                        taskActivity.start_date ?? undefined,
                                        taskActivity.start_time ?? undefined
                                    )}
                                />
                                <ReadOnlyField
                                    label="วันและเวลาสิ้นสุด"
                                    value={formatDateTimeString(
                                        taskActivity.end_date ?? undefined,
                                        taskActivity.end_time ?? undefined
                                    )}
                                />
                                <ReadOnlyField
                                    label="ความสำคัญ"
                                    value={task.priority}
                                />
                                <ReadOnlyField
                                    label="ความเร่งด่วน"
                                    value={hasteDescription}
                                />
                                {(taskActivity.time_duration || taskActivity.use_time) && (
                                    <ReadOnlyField
                                        label="ระยะเวลาดำเนินการ"
                                        value={taskActivity.time_duration || taskActivity.use_time}
                                    />
                                )}
                                {taskActivity.delay && (
                                    <ReadOnlyField
                                        label="ความล่าช้า"
                                        value={taskActivity.delay}
                                    />
                                )}
                            </div>
                        </section>

                        <section className="border-t pt-5">
                            <p className="mb-2 text-base font-semibold text-neutral-800">
                                แชทเรื่องงาน
                            </p>
                            {conversationActivities.length ? (
                                <div
                                    ref={chatListRef}
                                    className="max-h-[420px] space-y-3 overflow-y-auto pr-2 [scrollbar-gutter:stable]"
                                >
                                    {conversationActivities.map((activity, index) => (
                                        <ParticipantMessage
                                            key={activity.key || index}
                                            task={task}
                                            activity={activity}
                                            isOwnMessage={
                                                Boolean(currentLinkId) &&
                                                activity.creatorLinkID?.trim().toLowerCase() ===
                                                    currentLinkId.trim().toLowerCase()
                                            }
                                            onReplyEmail={onReplyEmail}
                                            onSelectCommentReply={setReplyTarget}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="rounded-md border border-neutral-200 bg-neutral-50 px-3 py-4 text-sm text-neutral-500">
                                    ยังไม่มีข้อความ
                                </div>
                            )}

                            <div className="mt-4 rounded-lg border border-neutral-200 bg-neutral-50 p-3">
                                {replyTarget && (
                                    <div className="mb-3 flex items-start gap-3 rounded-md border-l-4 border-l-primary-900 bg-white px-3 py-2 text-sm">
                                        <div className="min-w-0 flex-1">
                                            <p className="font-medium text-neutral-700">
                                                กำลังตอบกลับ {replyTarget.creatorFullname || 'ข้อความ'}
                                            </p>
                                            <p className="truncate text-xs text-neutral-500">
                                                {replyTarget.messageText ||
                                                    replyTarget.remarkEventDetail ||
                                                    replyTarget.eventMessage ||
                                                    '-'}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setReplyTarget(undefined)}
                                            className="shrink-0 text-xs text-neutral-500 hover:text-neutral-800"
                                        >
                                            ยกเลิก
                                        </button>
                                    </div>
                                )}

                                <textarea
                                    value={commentMessage}
                                    maxLength={3000}
                                    rows={3}
                                    placeholder="เขียนข้อความตอบกลับ..."
                                    onChange={event => setCommentMessage(event.target.value)}
                                    className="w-full resize-y rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-primary-900 focus:ring-1 focus:ring-primary-900"
                                />
                                <div className="mt-2 flex items-center justify-between gap-3">
                                    <span className="text-xs text-neutral-400">
                                        {commentMessage.length}/3000
                                    </span>
                                    <div className="flex items-center gap-3">
                                        {!onSubmitComment && (
                                            <span className="text-xs text-neutral-400">
                                                รอเชื่อมต่อ API
                                            </span>
                                        )}
                                        <Button
                                            disabled={
                                                !onSubmitComment ||
                                                !commentMessage.trim() ||
                                                isSubmittingComment
                                            }
                                            onClick={() => void handleSubmitComment()}
                                            className="bg-primary-900 text-white disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            {isSubmittingComment ? 'กำลังส่ง...' : 'ส่งข้อความ'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </ModalBody>

                <ModalFooter className="border-t px-6 py-4">
                    <Button
                        outline
                        onClick={onClose}
                        className="border-primary-900 text-primary-900"
                    >
                        ปิด
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default memo(TicketDetailModal);
